import { PreferenceKeys } from '@/Core/config/preferences';
import fullName from '@/Core/utilities/fullName';
import { DEFAULT_UNSET_OPTION, DEFAULT_UNSET_OPTION_NAME } from '@/Core/utilities/index';
import axios from 'axios';
import isEqual from 'lodash.isequal';
import { createNamespacedHelpers, mapActions, mapState, mapMutations } from 'vuex';
import contactInformationFields, {
  SHARED_WITH_SUB_ACCOUNTS_FIELD,
  SHARED_WITH_TEAM_FIELD,
  SHARED_WITH_TEAM_FIELD_V2,
  SHARED_WITH_SUB_ACCOUNTS_FIELD_PROFILE_V2,
} from '../config/contact-information-fields';
import {
  createTagsString,
  emailErrorMessages,
  primaryEmailLabel,
  validateEmails,
} from '../utilities';

const {
  mapActions: mapUserPreferencesActions,
  mapState: mapUserPreferencesState,
} = createNamespacedHelpers('userPreferences');

export default {
  data() {
    return {
      broker: {},
      validations: {
        isNumber: value => {
          // allow blank values to pass validation
          if (value) {
            return (
              /^\d*$/.test(value) || 'Please enter an integer (no decimal points) for this field'
            );
          }
          return true;
        },
        isDollar: value => {
          if (value) {
            return (
              /^\d*\.\d{2}$/.test(value) ||
              'Please enter a dollar amount (e.g. 999.00) for this field'
            );
          }
          return true;
        },
      },
      invalidFields: [],
    };
  },
  computed: {
    ...mapUserPreferencesState([PreferenceKeys.ContactFieldFavorites]),
    ...mapState('contact', ['contactMixinIsInitialized']),
    contactInformationError() {
      if (this.error) {
        return this.error;
      }
      if (this.contactError) {
        return this.contactError;
      }
      if (this.startCampaignError) {
        return this.startCampaignError;
      }
    },
    isHotnessEmpty() {
      if (this.selectedHotness) {
        return this.selectedHotness.name === DEFAULT_UNSET_OPTION_NAME;
      } else if (this.contact) {
        return Object.keys(this.contact.hotness).length === 0;
      }
      return true;
    },
    contactFullName() {
      return fullName(this.contactCopy);
    },
    contact() {
      return this.contactCopy;
    },
    isContactOwner() {
      return this.myInformation.id === this.currentContact.owner_user_id;
    },
    isAssignedUser() {
      return this.myInformation.id === this.currentContact.assigned_user_id;
    },
    /**
     * Don't want to include No One as an option for the owner user, as there
     * must always be an owner user.
     */
    ownerUsers() {
      if (!this.contactCopy || !this.contactCopy.contactOwner) return this.teamMembersInfo;
      
      const contactOwnerInfo = {
        value: this.contactCopy.contactOwner.id,
        text: `${this.contactCopy.contactOwner.fullName}`,
      }
      return [contactOwnerInfo, ...this.teamMembersInfo];
    },
    /**
     * In the case of assigned users, we do want No One as an option
     */
    assignedUsers() {
      return [DEFAULT_UNSET_OPTION, ...this.teamMembersInfo];
    },
    subAccounts() {
      return this.subAccountsInfo;
    },
    selectedTags: {
      get() {
        if (!this.contact.tags) return [];
        return this.contact.tags.split(',');
      },
      set(newValue) {
        this.contact.tags = createTagsString(newValue);
      },
    },
    baseFields() {
      return contactInformationFields(
        this.contactTypes, this.statuses, this.sources, this.ownerUsers, this.assignedUsers,
      );
    },
    fields() {
      const allFields = this.addSharedWithTeamField(this.baseFields);
      const curatedFields = this.addSharedWithSubAccountsField(allFields);
      return curatedFields;
    },
    contactFields() {
      const contactCustomFields = {
        hotness_id: 'hotness_id',
        hotness: 'Hotness',
        primary_email: 'Primary Email',
        email: 'email',
        secondary_email: 'Secondary Email',
        secondary: 'secondary_email',
      };
      return [
        {
          text: contactCustomFields.hotness,
          value: contactCustomFields.hotness_id,
          label: contactCustomFields.hotness,
          name: contactCustomFields.hotness_id,
          type: 'select',
          items: this.hotnesses,
          icon: 'whatshot',
          visible: false,
          custom: false,
        },
        {
          text: contactCustomFields.primary_email,
          value: contactCustomFields.email,
          label: contactCustomFields.primary_email,
          name: contactCustomFields.email,
          icon: contactCustomFields.email,
          visible: false,
          custom: false,
        },
        {
          text: contactCustomFields.secondary_email,
          value: contactCustomFields.secondary,
          label: contactCustomFields.secondary_email,
          name: contactCustomFields.secondary,
          icon: 'email',
          visible: false,
          custom: false,
        },
      ];
    },
    fieldsForFavorites: {
      get() {
        // Return the local storage value, if it is set
        if (this.favoriteFields && this.favoriteFields.length) {
          if (!this.currentContact.custom_fields
            || !this.currentContact.custom_fields.length) return this.favoriteFields;
          
          const customFields = this.currentContact.custom_fields.map(field => {
            return { ...field, text: field.name, visible: false, custom: true };
          });
          const favoriteFieldsCustomFields = this.favoriteFields.filter(field => field.custom);
          if (isEqual(customFields, favoriteFieldsCustomFields)) {
            return this.favoriteFields;
          }
          const filteredFavorites = this.favoriteFields.filter(field => !field.custom);
          for (let field of customFields) {
            const favoriteField = this.favoriteFields.find(favoriteField => field.id == favoriteField.id);
            if (favoriteField && favoriteField.visible) {
              field.visible = favoriteField.visible;
            }
          }
          return [...filteredFavorites, ...customFields];
        }

        const { misc, personal, phones } = this.baseFields;

        // Format the fields for dynamic rendering in the favorites section
        const general = [...misc, ...personal, ...phones];
        let fields = general.map(field => {
          if (['first_name', 'last_name'].includes(field.name)) {
            field.icon = 'person';
          }
          return {
            ...field,
            text: field.label,
            value: field.name,
            visible: false,
            custom: false,
          };
        });
        fields = fields.filter(field => field.isFavorited !== false);

        // Return with formatted custom_fields if they exist
        try {
          const custom = this.contact.custom_fields.map(field => {
            return { ...field, text: field.name, visible: false, custom: true };
          });
          return [...this.contactFields, ...fields, ...custom];
        } catch (err) {
          return [...this.contactFields, ...fields];
        }
      },
      set(fields) {
        // Set favoriteFields to empty array if no favorite fields selected
        if (!fields.find(f => f.visible)) {
          this.favoriteFields = [];
          return;
        }
        this.favoriteFields = fields;
      },
    },
    // Return favorited fields that are not addresses
    filteredFavorites() {
      return this.favoriteFields
      .reduce((acc, field) => {
        if (!field.custom) return [...acc, field];
        const res = this.initialCustom.find(f => f.id === field.id);
        if (res && res.value) {
          return [...acc, { ...field, value: res.value }];
        }
        return [...acc, field];
      }, [])
      .filter(f => f.visible && f.type !== 'address')
      .filter(field => {
        if (!field.custom) return !field.custom;
        const customField = this.contactCopy.custom_fields.find(customField => customField.id == field.id);
        if (customField) {
          field.value = customField.value;
        }
        return customField;
      });
    },
    // Return addresses if selected as a favorite
    addressFavorites() {
      if (this.favoriteFields.filter(f => f.visible && f.type === 'address').length) {
        return this.contact.addresses;
      }
      return [];
    },
    labels() {
      const labels = {};
      Object.keys(this.baseFields).forEach(type => {
        this.baseFields[type].forEach(field => {
          labels[field.label] = field.label;
        });
      });
      return labels;
    },
    hotnessColour() {
      const { selectedHotness, contact } = this;
      try {
        return selectedHotness ? selectedHotness.color : contact.hotness.color;
      } catch (err) {
        return null;
      }
    },
    myInformation() {
      if (this.currentUserInfo && this.currentUserInfo.data) {
        return this.currentUserInfo.data.me;
      }
      return {};
    },
  },
  watch: {
    currentContact: {
      handler(newContact) {
        if (!this.initialContact || this.initialContact.id !== newContact.id) {
          this.showSave = false;
          this.userHasChanges = false;
          this.resetPatchedFields();
          this.initialContact = JSON.parse(JSON.stringify(newContact));
          this.initialAddresses = JSON.parse(JSON.stringify(newContact.addresses));
          this.initialCustom = JSON.parse(JSON.stringify(newContact.custom_fields));
        }
        newContact.custom_fields.forEach(field => {
          if (field.data_type === 'Date') {
            field.value = this.formatCustomFieldsDates(field.value)
          }
        });
        this.contactCopy = JSON.parse(JSON.stringify(newContact));
      },
      deep: true,
    },
    saveContactSuccess(bool) {
      // Success bar was hidden and 'undo' not clicked
      if (!bool && this.resetInitial) {
        this.resetLastPatch();
      }
      this.resetInitial = true;
    },
  },
  async created() {
    if (this.contactMixinIsInitialized) {
      this.loadFavorites();
      return;
    }
    this.setContactMixinIsInitialized(true);
    this.clearContactTableMessage();
    await this.findCurrentUser();
    await this.findCustomFieldTypes();
    await this.findAddressTypes();
    await this.loadContactSources();
    await this.findTeamMembers();
    await this.findSubAccounts();
    await this.loadContactTypes();
    await this.loadContactHotnesses();
    await this.findUserPreferences({ key: PreferenceKeys.ContactFieldFavorites });
    this.loadFavorites();
  },
  async mounted() {
    try {
      const brokerData = await axios.get(`${process.env.VUE_APP_ACCOUNTS_API}/users/broker`);
      this.broker = { ...brokerData.data };
    } catch (error) {
      this.broker = { ...error };
    }
  },
  methods: {
    ...mapUserPreferencesActions(['findUserPreferences', 'saveUserPreferences']),
    ...mapActions('contactsTable', ['clearContactTableMessage']),
    ...mapActions('contact', ['findCurrentUser', 'findAddressTypes','loadContactSources','loadContactTypes','loadContactHotnesses']),
    ...mapActions('customFields', ['findCustomFieldTypes']),
    ...mapActions('teamMembers', ['findTeamMembers']),
    ...mapActions('subAccounts', ['findSubAccounts']),
    ...mapMutations('contact', ['setContactMixinIsInitialized']),

    formatCustomFieldsDates(date) {
      if (!date) return null;

      const CORRECT_FORMAT_REGEX = /^(0[1-9]|10|[0-1][1-2])-(0[1-9]|10|[1-2][0-9]|3[0-1])-\d{4}$/;
      const PREVIOUS_FORMAT_REGEX = /^\d{4}-([1-9]|10|[0-1][1-2])-([1-9]|10|[1-2][0-9]|3[0-1])$/;
      const splitDate = date.split(/\D/g);
      
      if (CORRECT_FORMAT_REGEX.test(date)) return date;

      if (PREVIOUS_FORMAT_REGEX.test(date)) {
        const MONTH = splitDate[1].length == 1 ? `0${splitDate[1]}` : splitDate[1];
        const DAY = splitDate[2].length == 1 ? `0${splitDate[2]}` : splitDate[2];
        
        return `${MONTH}-${DAY}-${splitDate[0]}`;
      }
      return date;
    },
    // get custom field validation rule based on custom field data type
    customFieldValidation(dataType) {
      switch (dataType) {
        case 'Dollar Amt':
          return 'isDollar';
        case 'Number':
          return `is${dataType}`;
        default:
      }
    },
    closeCustomFieldDialog() {
      this.customFieldDialog = false;
      this.newCustomField = {};
    },
    findIndexById(arrayName, targetId) {
      return arrayName.findIndex(field => field.id === targetId);
    },
    /**
     * This function will clear fields in the patch if the user clears a field
     * during their edit phase and submits.
     *
     * @param  {String} attrName the attribute name
     * @return {}
     */
    clearFieldInPatch(attrName) {
      if (!this.patchedParams[attrName]) {
        this.patchedParams[attrName] = null;
      }
    },
    addToPatchedFields(newValue, attrName) {
      this.fieldValueErrorTypes = [];
      this.fieldValueErrorMessages = {};
      this.showSave = true;
      if (this.isProfileV2 && attrName == 'addresses') {
        var addressName = newValue.name;
        delete newValue.name;
      }
      switch (attrName) {
        // check if attrName is custom_fields or addresses
        case 'custom_fields': {
          const fields = this.contactCopy[attrName];
          const index = this.findIndexById(fields, newValue.id);
          if(index > 0) {
            fields[index].value = newValue.value;
            fields[index].id = newValue.id;
          }

          // update params that will be sent with patch request--order doesn't matter
          this.patchedParams[attrName] = this.patchedParams[attrName].filter(field => {
            return field.id !== newValue.id;
          });
          const { id, value } = newValue;
          if(newValue.id > 0) {
            this.patchedParams[attrName].push({ id, value });
          } else {
            this.patchedParams[attrName].push(newValue);
          }

          if (!this.lastPatch[attrName].find(f => f.id === newValue.id)) {
            const field = this.initialCustom.find(f => f.id === newValue.id);
            const customField = field ? { id: field.id, value: field.value } : { id: newValue.id, value: null };
            this.lastPatch[attrName].push(customField);
          }
          this.userHasChanges = true;
          // We don't have a removal mechanism for custom fields, so don't add to lastPatch
          break;
        }
        case 'addresses': {
          // preserve the order in which addresses are displayed on UI
          const fields = this.contactCopy[attrName];
          const index = this.findIndexById(fields, newValue.id);
          fields[index] = {...fields[index], ...newValue};

          // update params that will be sent with patch request--order doesn't matter
          this.patchedParams[attrName].write = this.patchedParams[attrName].write.filter(field => {
            return field.id !== newValue.id;
          });
          this.patchedParams[attrName].write.push(fields[index]);

          if (!this.lastPatch[attrName].write.find(field => field.id === newValue.id)) {
            // Address not yet in lastPatch, so add it
            const address = this.initialAddresses.find(field => field.id === newValue.id);
            if (address) {
              this.lastPatch[attrName].write.push(address);
            }
          }
          break;
        }
        case 'status': {
          if (newValue === DEFAULT_UNSET_OPTION_NAME) {
            this.patchedParams[attrName] = null;
          } else {
            this.patchedParams[attrName] = newValue;
          }
          this.contactCopy[attrName] = newValue;
          this.lastPatch[attrName] = this.initialContact[attrName];
          break;
        }
        default:
          // Ensure that newValue is defined
          if (typeof newValue !== 'undefined') {
            this.contactCopy[attrName] = newValue;
            this.patchedParams[attrName] = newValue;
          } else {
            // Patch with null to avoid validation errors if not defined
            this.contactCopy[attrName] = null;
            this.patchedParams[attrName] = null;
          }
          this.lastPatch[attrName] = this.initialContact[attrName];
      }
      if (this.isProfileV2) {
        switch(attrName) {
          case 'custom_fields':
            if (!this.unsavedChanges.includes(newValue.name)) {
              this.unsavedChanges.push(newValue.name);
              this.userHasChanges = true;
            }
            break;
          case 'addresses':
            const addressFieldName = `${newValue.id} ${addressName}`
            if (!this.unsavedChanges.includes(addressFieldName)) {
              this.unsavedChanges.push(addressFieldName);
              this.userHasChanges = true;
            }
            break;
          case 'contacts_relationships':
            newValue.forEach( relationship => {
              if (!this.unsavedChanges.includes(relationship.relationship_id)) {
                this.unsavedChanges.push(relationship.relationship_id);
                this.userHasChanges = true;
              }
            });

            break;
          default:
            if (!this.unsavedChanges.includes(attrName)) {
              this.unsavedChanges.push(attrName);
              this.userHasChanges = true;
            }
            break;
        }
      }
    },
    addNewCustomField() {
      // all dynamically added custom fields have negative ID values
      let { data_type, name } = this.newCustomField;
      this.newCustomFieldId--;
      const newCustomField = { id: this.newCustomFieldId, name, data_type };
      const customFieldsCopy = Object.assign([], this.contact.custom_fields);
      customFieldsCopy.push(newCustomField);
      this.contact.custom_fields = customFieldsCopy;
      this.patchedParams.custom_fields.push(newCustomField);
      if (this.unsavedChanges && !this.unsavedChanges.includes(name)) {
        this.unsavedChanges.push(name);
        this.userHasChanges = true;
      }
      this.closeCustomFieldDialog();
    },
    addNewAddress() {
      // all dynamically added addresses have negative ID values
      this.newAddrId--;
      const newAddr = { id: this.newAddrId };
      const addressCopy = Object.assign([], this.contact.addresses);
      addressCopy.push(newAddr);
      this.contact.addresses = addressCopy;
      this.patchedParams.addresses.write.push(newAddr);
    },
    removeAddress(address) {
      const { addresses } = this.contact;
      const addrIndex = this.findIndexById(addresses, address.id);
      const fullAddress = addresses[addrIndex];
      this.contact.addresses = addresses.filter((ele, idx) => idx !== addrIndex);

      if (address.id > 0) {
        this.patchedParams.addresses.remove.push(address.id);
        // Add a new instance of the removed address to the write array for reverting to
        this.newAddrId--;
        this.lastPatch.addresses.write.push({ ...fullAddress, id: this.newAddrId });
      }
      this.showSave = true;
      if(this.isProfileV2) {
        this.userHasChanges = true;
        this.unsavedChanges.push("addresses");
      }
    },
    resetPatchedFields() {
      this.patchedParams = {
        addresses: {
          write: [],
          remove: [],
        },
        custom_fields: [],
      };
    },
    resetLastPatch() {
      // 'Undo' not clicked so set initials to current value for future revert
      if (this.resetInitial) {
        this.contactCopy.addresses.forEach(address => { delete address.isNewAddress });
        this.initialContact = JSON.parse(JSON.stringify(this.contactCopy));
        this.initialAddresses = this.initialContact.addresses.filter(address => address.id >= 0);
        this.initialCustom = this.initialContact.custom_fields;
      }
      // Clear lastPatch
      this.lastPatch = {
        addresses: {
          write: [],
          remove: [],
        },
        custom_fields: [],
        custom_fields_remove: [],
      };
    },
    async saveContact() {
      if (this.isProfileV2) {
        const { email: primaryEmail, secondary_email: secondaryEmail, spouse_email: spouseEmail } = this.contactCopy;

        const { 
          'section-primary_details': primaryDetails, 
          'section-spouse_information': spouseInformation,
        } = this.$refs;

        const { 
          email: primaryEmailField, 
          secondary_email: secondaryEmailField 
        } = primaryDetails[0].$refs['container-primary_details'].$refs['primary_details'].$refs;

        const { 
          spouse_email: spouseEmailField 
        } = spouseInformation[0].$refs['container-spouse_information'].$refs['spouse_information'].$refs;

        if (primaryEmailField || secondaryEmailField || spouseEmailField) {
          validateEmails(
            this, 
            primaryEmail, 
            secondaryEmail,
            spouseEmail,
            primaryEmailField ? primaryEmailField[0] : null, 
            secondaryEmailField ? secondaryEmailField[0] : null,
            spouseEmailField
          );
        }
      } else {
        const { email: primaryEmail, secondary_email: secondaryEmail, spouse_email: spouseEmail } = this.contact;
        const { primaryEmailField, secondaryEmailField, spouseEmailField } = this.$refs;
        validateEmails(
          this,
          primaryEmail,
          secondaryEmail,
          spouseEmail,
          primaryEmailField,
          secondaryEmailField,
          spouseEmailField
        );
      }

      if (!this.fieldValueErrorTypes.length) {
        try {
          await this.patchContact(this.patchedParams);
          if (!this.hasError) {
            this.showSave = false;
            this.resetPatchedFields();
            this.saveContactSuccess = true;
            this.userHasChanges = false;
            this.unsavedChanges = [];
            this.setShouldReloadContactsTable();
            this.resetRelationshipsTypeChanges();
            this.fetchContactRelationships(this.$route.params.id);
          }
        } catch (err) {
          if (this.updateContactError && this.updateContactErrorCode === 403) {
            if (err.message.match(new RegExp(emailErrorMessages.duplicateAddress))) {
              this.fieldValueErrorTypes.push(primaryEmailLabel);
              this.fieldValueErrorMessages[primaryEmailLabel] = emailErrorMessages.duplicateAddress;
              primaryEmailField.focus();
            } else {
              this.$router.push({ name: 'contacts' });
              return;
            }
          }
          this.error = err.errors ? err.errors[0] : err;
        }
      }
    },
    async revertPatch() {
      // Undo was clicked, so don't reset initial values
      this.resetInitial = false;
      // Hide snackbar
      this.saveContactSuccess = false;
      try {
        if (this.contactCopy.addresses) {
          const addressesDiff = this.contactCopy.addresses.filter(({ id: addressId }) => !this.initialAddresses.some(({ id: initialAddressId }) => addressId === initialAddressId));

          addressesDiff.forEach(address => {
            if (address.isNewAddress) {
              this.lastPatch.addresses.remove.push(address.id)
            }
          });
        }

        if (this.contactCopy.custom_fields) {
          const customFieldsDiff = this.contactCopy.custom_fields.filter(({ id: customFieldId }) => !this.initialCustom.some(({ id: initialcustomFieldId }) => customFieldId === initialcustomFieldId));
          if(customFieldsDiff.length > 0) {
            customFieldsDiff.forEach(field => {
              this.lastPatch.custom_fields_remove.push(field.id);
            });
            this.lastPatch.custom_fields = this.lastPatch.custom_fields.filter(field => field.id > 0);
          }
        }

        await this.patchContact(this.lastPatch);

        this.resetLastPatch();

        // Ensure initial address values have the appropriate ids
        this.initialAddresses = this.currentContact.addresses.map((address, i) => {
          return { ...this.initialAddresses[i], id: address.id };
        });

        // Ensure initial custom field values have the appropriate value and id
        this.initialCustom = this.contactCopy.custom_fields.map((custom, i) => {
          return { ...this.initialCustom[i], id: custom.id, value: custom.value };
        });
        
      } catch (err) {
        this.error = err.errors ? err.errors[0] : err;
      }
    },
    // Reset error state for email validations
    resetEmailValidationErrors() {
      this.fieldValueErrorTypes = [];
      this.fieldValueErrorMessages = {};
    },
    cancelUpdate() {
      this.resetEmailValidationErrors();
      this.showSave = false;
      this.resetPatchedFields();
      this.contactCopy = JSON.parse(JSON.stringify(this.currentContact));
      this.selectedHotness = null;
      this.userHasChanges = false;
      this.unsavedChanges = [];
      this.resetRelationships();
    },
    toggleEditMode(action = null) {
      this.isEditMode = !this.isEditMode;

      if (action === 'cancel') {
        // if cancelling an edit, clear all changes
        this.resetPatchedFields();
        this.contactCopy = JSON.parse(JSON.stringify(this.currentContact));
        this.selectedHotness = null;
      }
    },
    subAccountCanEdit(field) {
      const { subAccount } = this.currentUserInfo;
      switch (field) {
        case 'source':
          return subAccount.is_lead_sources_enabled || subAccount.is_lead_sources_new_enabled;
        case 'custom field':
          return subAccount.is_custom_fields_enabled || subAccount.is_custom_fields_new_enabled;
        default:
          return true;
      }
    },
    canEdit(field) {
      return this.isContactOwner || this.isAssignedUser || this.subAccountCanEdit(field);
    },
    saveTags(newTags = '') {
      const savedTags = this.isProfileV2 ? newTags : this.contact.tags;
      this.addToPatchedFields(savedTags, 'tags');
    },
    contactUpdated() {
      this.$emit('contact-updated');
    },
    updateHotness(hotnessId) {
      this.hotnesses.forEach(hotness => {
        if (hotness.value === hotnessId) {
          this.selectedHotness = { color: hotness.color, name: hotness.text };
        }
      });
    },
    setFavorites(fields) {
      const value = fields.map(f => {
        if (f.custom) {
          return { ...f, value: null };
        }
        return f;
      });
      this.saveUserPreferences({ key: PreferenceKeys.ContactFieldFavorites, value });
      localStorage.setItem(PreferenceKeys.ContactFieldFavorites, JSON.stringify(value));
    },
    isTextField(field) {
      return !field.custom && field.type !== 'address' && field.name !== 'hotness_id';
    },
    phoneEmailAction(value) {
      // Tests if passed value is a phone number or email address
      const phone = new RegExp(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/g);
      const isPhone = phone.test(value);
      const email = new RegExp(
        /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/g,
      );
      const isEmail = email.test(value);

      // Handle communications based on value type
      if (isPhone) {
        this.setToSelected([
          {
            id: this.contact.id || this.contact.name.id,
            mobile_phone: this.contact.mobile_phone,
            first_name: this.contact.first_name,
            last_name: this.contact.last_name,
          },
        ]);
        if (this.isProfileV2) {
          this.showContactChat();
        } else {
          this.$emit('showChatDrawer');
        }
      } else if (isEmail) {
        this.setToSelected([
          {
            id: this.contact.id || this.contact.name.id,
            email: value,
            first_name: this.contact.first_name,
            last_name: this.contact.last_name,
          },
        ]);
        if (this.isProfileV2) {
          this.openEmailDrawer();
        } else {
          this.$emit('openEmailDrawer');
        }
      }
    },
    loadFavorites() {
      const dbFavorites = this[PreferenceKeys.ContactFieldFavorites];
      let localFavorites = localStorage[PreferenceKeys.ContactFieldFavorites]
        ? JSON.parse(localStorage[PreferenceKeys.ContactFieldFavorites])
        : [];
      if (dbFavorites.length > 0 && !isEqual(dbFavorites, localFavorites)) {
        localFavorites = dbFavorites;
      }
      // make deep copy to avoid mutating state
      localFavorites = JSON.parse(JSON.stringify(localFavorites));
      // Update sources and hotnesses items to response from DB
      localFavorites.forEach(field => {
        if (field.name === 'source_id') {
          field.items = this.sources;
        } else if (field.name === 'hotness_id') {
          field.items = this.hotnesses;
        } else if (field.name === 'type_id') {
          field.items = this.contactTypes;
        } else if (field.name === 'assigned_user_id') {
          field.items = this.assignedUsers;
        }
      });
      this.favoriteFields = localFavorites;
    },
    addSharedWithTeamField(fields) {
      const currentUserId = this.currentUser.data ? this.currentUser.data.me.id : null;
      const contactOwnerUserId = this.contact.owner_user_id;

      if (
        (currentUserId === contactOwnerUserId &&
          fields && fields.misc)
      ) {
        return fields;
      }

      const fieldsWithoutShareWithTeam = fields.misc
        .filter(fieldObj => fieldObj.label !== SHARED_WITH_TEAM_FIELD.label);

      const filteredFields = Object.assign({}, fields, { misc: fieldsWithoutShareWithTeam });
      return filteredFields;
    },
    addSharedWithTeamFieldV2(fields) {
      const currentUserId = this.currentUser.data ? this.currentUser.data.me.id : null;
      const contactOwnerUserId = this.contact.owner_user_id;
      if(currentUserId === contactOwnerUserId) {
        const primaryDetails = fields.find(field => field.card_id === "primary_details");
        primaryDetails.fields.push({
          ...SHARED_WITH_TEAM_FIELD_V2,
          value: this.contactCopy.is_hidden_from_team
        });
      }
    },
    addSharedWithSubAccountsField(fields) {
      const currentUserId = this.currentUser.data ? this.currentUser.data.me.id : null;

      if (
        currentUserId &&
        !this.broker.error &&
        this.broker.idBroker &&
        currentUserId === this.contact.owner_user_id &&
        fields && fields.misc
      ) {
        if (fields.misc.find(fieldObj => fieldObj.label === SHARED_WITH_SUB_ACCOUNTS_FIELD.label)) {
          const fieldsSubAccount = fields.misc.map(fieldObj => {
            if (fieldObj.label === SHARED_WITH_SUB_ACCOUNTS_FIELD.label) {
              fieldObj.items = this.subAccounts;
            }
            return fieldObj;
          });

          const fieldsWithSubAccounts = Object.assign({}, fields, { misc: fieldsSubAccount });
          return fieldsWithSubAccounts;
        }
      } else {
        const fieldsWithoutSubAccount = fields.misc
          .filter(fieldObj => fieldObj.label !== SHARED_WITH_SUB_ACCOUNTS_FIELD.label);

        const filteredFields = Object.assign({}, fields, { misc: fieldsWithoutSubAccount });
        return filteredFields;
      }
    },
    addSharedWithSubAccountsFieldProfileV2(fields) {
      const currentUserId = this.currentUser.data ? this.currentUser.data.me.id : null;
      const primaryDetails = fields.find(field => field.card_id === "primary_details");
      if (
        currentUserId &&
        !this.broker.error &&
        this.broker.idBroker &&
        currentUserId === this.contact.owner_user_id &&
        primaryDetails
      ) {
        const sharedWithField = {
          ...SHARED_WITH_SUB_ACCOUNTS_FIELD_PROFILE_V2,
          options: this.subAccounts,
          value: this.contactCopy.shared_users
        };
        primaryDetails.fields.push(sharedWithField);
      }
    },
    fieldStatusChange(field) {
      const fieldIndex = this.invalidFields.indexOf(field.name);
      if (fieldIndex === -1 && !field.isValid) {
        this.invalidFields.push(field.name);
      }
      if (fieldIndex > -1 && field.isValid){
        this.invalidFields.splice(fieldIndex, 1);
      }
    },
  },
};
